import { EmptyState, LoadingState } from '@vrzl/ui';
import { buildHighlightCandidates } from '@vrzl/ui/markdownPlainText';
import { FileX } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Document, Page } from 'react-pdf';

import { ApiError } from '@/domains/general/api/client';

import { useDocumentBlob } from '../api/queries';
import { resolveHighlightMode, type RangeMode, type TextItemLite } from '../lib/highlightMatch';

type Props = {
  eventId: string;
  docId: string;
  initialPages?: number[];
  highlightQuery?: string;
  selectedEvidence: { pages?: number[]; sentence?: string; plainSentence?: string } | null;
};

const MARK_OPEN = '<mark style="background:rgba(255,213,0,0.45);border-radius:2px;">';
const MARK_CLOSE = '</mark>';

export function DocumentPdfPanel({
  eventId,
  docId,
  initialPages,
  highlightQuery,
  selectedEvidence,
}: Props) {
  const { data: blob, isPending, error } = useDocumentBlob(eventId, docId);
  const [numPages, setNumPages] = useState(0);
  const [pageTexts, setPageTexts] = useState<Map<number, TextItemLite[]>>(() => new Map());
  const [settledPages, setSettledPages] = useState<Set<number>>(() => new Set());
  const containerRef = useRef<HTMLDivElement>(null);
  const hasScrolledRef = useRef(false);

  useEffect(() => {
    setPageTexts(new Map());
    setSettledPages(new Set());
    setNumPages(0);
    hasScrolledRef.current = false;
  }, [eventId, docId]);

  /** Een pagina is klaar met renderen en heeft dus haar definitieve hoogte. */
  const markPageSettled = useCallback((pageNumber: number) => {
    setSettledPages((prev) => {
      if (prev.has(pageNumber)) return prev;
      const next = new Set(prev);
      next.add(pageNumber);
      return next;
    });
  }, []);

  // Evidence-selectie levert kandidaten in prioriteitsvolgorde (backend-platte
  // tekst, dan client-side gestripte sentence als terugval); zonder selectie
  // valt terug op de eenvoudige zoekbalk-query.
  const candidates = useMemo(
    () =>
      selectedEvidence?.sentence || selectedEvidence?.plainSentence
        ? buildHighlightCandidates(selectedEvidence.sentence, selectedEvidence.plainSentence)
        : highlightQuery
          ? [highlightQuery]
          : [],
    [selectedEvidence, highlightQuery],
  );
  const targetPage = selectedEvidence?.pages?.[0] ?? initialPages?.[0];

  const highlightMode = useMemo<RangeMode | null>(
    () => resolveHighlightMode(pageTexts, candidates, targetPage),
    [candidates, targetPage, pageTexts],
  );

  const textRenderer = useMemo(() => {
    const mode = highlightMode;
    if (!mode) return undefined;
    return ({
      pageNumber,
      itemIndex,
      str,
    }: {
      pageNumber: number;
      itemIndex: number;
      str: string;
    }): string => {
      if (pageNumber !== mode.pageNumber) return str;
      if (itemIndex < mode.startItem || itemIndex > mode.endItem) return str;
      return MARK_OPEN + str + MARK_CLOSE;
    };
  }, [highlightMode]);

  const effectiveTargetPage = highlightMode?.pageNumber ?? targetPage;

  useEffect(() => {
    if (numPages === 0 || !effectiveTargetPage) return;
    // Wacht tot de pagina's vóór het doel hun definitieve hoogte hebben; anders
    // verschuift de layout na het scrollen en belandt de viewer terug bovenaan.
    for (let page = 1; page <= effectiveTargetPage; page += 1) {
      if (!settledPages.has(page)) return;
    }
    const container = containerRef.current;
    if (!container) return;
    const target = container.querySelector<HTMLElement>(
      `[data-page-number="${effectiveTargetPage}"]`,
    );
    if (!target) return;
    target.scrollIntoView({
      behavior: hasScrolledRef.current ? 'smooth' : 'auto',
      block: 'start',
    });
    hasScrolledRef.current = true;
  }, [numPages, effectiveTargetPage, settledPages]);

  const isNotFound = error instanceof ApiError && error.status === 404;

  if (isPending) {
    return <LoadingState message="Document laden..." />;
  }

  if (isNotFound) {
    return (
      <EmptyState
        icon={FileX}
        title="Document niet gevonden"
        description="Dit document is niet beschikbaar of bestaat niet."
      />
    );
  }

  if (error) {
    return (
      <EmptyState
        icon={FileX}
        title="Kon PDF niet laden"
        description="Probeer het later opnieuw."
      />
    );
  }

  if (!blob) return null;

  return (
    <div ref={containerRef} className="h-full overflow-y-auto bg-muted/30 p-4">
      <Document
        file={blob}
        onLoadSuccess={({ numPages: n }) => setNumPages(n)}
        loading={<LoadingState message="Pagina laden..." />}
      >
        {Array.from({ length: numPages }).map((_, i) => {
          const pageNumber = i + 1;
          return (
            <div
              key={pageNumber}
              data-page-number={pageNumber}
              className="mx-auto mb-3 w-fit"
            >
              <Page
                pageNumber={pageNumber}
                renderTextLayer
                renderAnnotationLayer
                customTextRenderer={textRenderer}
                onRenderSuccess={() => markPageSettled(pageNumber)}
                onRenderError={() => markPageSettled(pageNumber)}
                onGetTextSuccess={(textContent) => {
                  const items: TextItemLite[] = (textContent.items as unknown[]).map((raw) => {
                    if (typeof raw === 'object' && raw !== null && 'str' in raw) {
                      return { str: String((raw as { str: unknown }).str) };
                    }
                    return { str: '' };
                  });
                  setPageTexts((prev) => {
                    const existing = prev.get(pageNumber);
                    if (
                      existing &&
                      existing.length === items.length &&
                      existing.every((it, idx) => it.str === items[idx]?.str)
                    ) {
                      return prev;
                    }
                    const next = new Map(prev);
                    next.set(pageNumber, items);
                    return next;
                  });
                }}
              />
            </div>
          );
        })}
      </Document>
    </div>
  );
}
